const { getElements } = window.BAO.utils

const selectors = {
  addToCart: '[data-product-add-to-cart]',
  addToCartText: '[data-product-add-to-cart-text]',
  backInStockForm: '[data-product-back-in-stock-form]',
  backInStockToggle: '[data-product-back-in-stock-toggle]',
  form: '[data-product-form]',
  price: '[data-product-price]',
  priceCompare: '[data-product-price-compare]',
  priceCompareText: '[data-product-price-compare-text]',
  sku: '[data-product-sku]',
  title: '[data-product-variant-title]',
}

export class Variant {
  constructor (data) {
    /**
     * @type {ShopifyVariant}
     * @public
     */
    this.data = data
  }

  setPrice () {
    unhidePriceEls()

    const priceElements = window.BAO.utils.getElements(selectors.price)
    priceElements.forEach(el => {
      el.innerHTML = window.BAO.utils.formatMoney(this.data.price)
      el.classList.toggle(
        'prd-DetailForm_Price-sale',
        this.data.price < this.data.compare_at_price,
      )
    })

    const priceCompareElements = window.BAO.utils.getElements(
      selectors.priceCompare,
    )
    priceCompareElements.forEach(
      el =>
        (el.innerHTML = window.BAO.utils.formatMoney(
          this.data.compare_at_price,
        )),
    )

    if (!this.data.compare_at_price) {
      hidePriceCompareElements()
    }
  }

  setAvailability () {
    window.BAO.utils.getElements(selectors.addToCart).forEach(el => {
      el.disabled = false
      el.setAttribute('type', this.data.available ? 'submit' : 'button')
      el.toggleAttribute('data-out-of-stock', !this.data.available)
    })

    getElements(selectors.addToCartText).forEach(el => {
      el.textContent = this.data.available
        ? window.theme.strings.addToCart
        : window.theme.strings.soldOut
    })

    getElements(selectors.addToCart).forEach(el => {
      el.setAttribute('aria-hidden', !this.data.available)
    })

    getElements(selectors.backInStockToggle).forEach(el => {
      el.setAttribute('aria-hidden', this.data.available)
    })

    getElements(selectors.backInStockForm).forEach(el => {
      el.dataset.variantId = this.data.id
    })
  }

  setTitle () {
    window.BAO.utils
      .getElements(selectors.title)
      .forEach(el => (el.textContent = this.data.title))
  }

  updateInput () {
    const inputs = window.BAO.utils.getElements(
      'form[action*="/cart/add"] [name="id"]',
    )

    inputs.forEach(input => {
      input.value = this.id
      input.dispatchEvent(new Event('change', { bubbles: true }))
    })
  }

  setUrl () {
    window.history.replaceState(
      { path: this.url.toString() },
      '',
      this.url.toString(),
    )
  }

  setShareUrl () {
    const shareButtons = window.BAO.utils.getElements('share-button')
    if (shareButtons.length === 0) return

    shareButtons.forEach(shareButton => {
      shareButton.updateUrl(this.url.toString())
    })
  }

  /**
   * @returns {number}
   */
  get id () {
    return this.data.id
  }

  /**
   * @returns {URL}
   */
  get url () {
    // eslint-disable-next-line compat/compat
    const url = new URL(window.location.href)
    url.searchParams.set('variant', this.id.toString())

    return url
  }
}

function hideElements (elements) {
  elements.forEach(el => (el.hidden = true))
}

function unhideElements (elements) {
  elements.forEach(el => (el.hidden = false))
}

function unhidePriceEls () {
  unhideElements(
    window.BAO.utils.getElements(
      `${selectors.price}, ${selectors.priceCompare}`,
    ),
  )
}

function hidePriceCompareElements () {
  hideElements(window.BAO.utils.getElements(selectors.priceCompare))
  hideElements(window.BAO.utils.getElements(selectors.priceCompareText))
}

export class VariantSelect extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['data']
  }

  static get requiredAttributes () {
    return ['form-id']
  }

  constructor () {
    super()

    /**
     * @type {Variant[]}
     * @public
     */
    this.collectedVariants = [new Variant(this.defaultVariant)]
  }

  setupListeners () {
    super.setupListeners()

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PRODUCT.VARIANT_SELECTED,
      this.handleVariantSelection.bind(this),
    )
  }

  handleVariantSelection (event) {
    if (event.detail.formId !== this.getAttribute('form-id')) return

    this.currentVariant = event.detail.variant

    const variant = this.getVariant()

    variant.setAvailability()
    variant.setPrice()
    variant.setTitle()
    variant.updateInput()

    if (!this.currentVariant) {
      this.resetPage(variant)

      return
    }

    variant.setUrl()
    variant.setShareUrl()
  }

  /**
   * @param {Variant} variant
   *
   * @returns {void}
   */
  resetPage (variant) {
    window.history.replaceState(
      { path: window.location.pathname },
      '',
      window.location.pathname,
    )
  }

  /**
   * @param {ShopifyVariant} data
   *
   * @returns {Variant}
   */
  createVariant (data) {
    const variant = new Variant(data)

    return variant
  }

  /**
   * @returns {Variant}
   */
  getVariant () {
    return this.createVariant(this.currentVariant)
  }

  /**
   * @returns {ShopifyVariant}
   */
  get defaultVariant () {
    return this.data[0]
  }

  set currentVariant (data) {
    this._currentVariant = data
  }

  /**
   * @returns {ShopifyVariant|undefined}
   */
  get currentVariant () {
    return this._currentVariant || this.defaultVariant
  }

  /**
   * @returns {ShopifyVariant[]}
   */
  get data () {
    return JSON.parse(this.els.data.element.innerText)
  }
}

if (!customElements.get('variant-select')) {
  customElements.define('variant-select', VariantSelect)
}
